import { Component } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
    ValidationErrors,
    ValidatorFn,
} from '@angular/forms';
import { EnedisService } from '../../enedis.service';
import { AlertService } from '@ed---interne/ng-uui-components';
import {
    AdresseInstallationModel,
    EnedisSearchPointModel,
    SearchpointCritereModel,
} from '../../enedis.models';
import {
    DisplayedColumns,
    DisplayType,
} from 'src/app/shared/components/ed-table/ed-table.component';
import { MatTableDataSource } from '@angular/material/table';

@Component({
    selector: 'app-enedis-search',
    templateUrl: './enedis-search.component.html',
    styleUrls: ['./enedis-search.component.scss'],
})
export class EnedisSearchComponent {
    public isLoading: boolean = false;
    public resultTableDataSource: MatTableDataSource<any> = new MatTableDataSource<any>();
    public resultTableDisplayedColumns: DisplayedColumns[] = [
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'PDL',
            objectKey: 'pdlNumber',
            bold: true,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Adresse',
            objectKey: 'adresse',
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Etat contractuel',
            objectKey: 'etatContractuel',
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Numéro de série',
            objectKey: 'numeroSerie',
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Type de comptage',
            objectKey: 'typeComptage',
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Matricule',
            objectKey: 'matricule',
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Nom',
            objectKey: 'nomClientFinalOuDenominationSociale',
        },
    ];

    public searchFormGroup: FormGroup;
    public categorieClientFinalCodeOptions = [
        {
            value: 'RES',
            label: 'Résidentiel',
        },
        { value: 'PRO', label: 'Professionnel' },
    ];

    public domaineTensionOptions = [
        { value: 'BTINF', label: 'BT < 36kVA' },
        { value: 'BTSUP', label: 'BT >= 36kVA' },
        { value: 'HTA', label: 'HTA' },
        { value: 'HTB', label: 'HTB' },
    ];

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly enedisService: EnedisService,
        private readonly alertService: AlertService,
    ) {
        this.searchFormGroup = this.formBuilder.group(
            {
                escalierEtEtageEtAppartement: [undefined],
                batiment: [undefined],
                numeroEtNomVoie: [undefined],
                lieuDit: [undefined],
                codePostal: [undefined, [Validators.pattern(/^\d{5}$/)]],
                codeInseeCommune: [undefined, [Validators.pattern(/^\d{5}$/)]],
                numSiret: [undefined, [Validators.pattern(/^\d{14}$/)]],
                matriculeOuNumeroSerie: [undefined],
                domaineTensionAlimentationCode: [
                    undefined,
                    [this.inArrayValidator(['BTINF', 'BTSUP', 'HTA', 'HTB'])],
                ],
                nomClientFinalOuDenominationSociale: [undefined],
                categorieClientFinalCode: [undefined, [this.inArrayValidator(['PRO', 'RES'])]],
                rechercheHorsPerimetre: [undefined], // booléen
            },
            {
                validators: [
                    siretOuAdresseValidator(),
                    rechercheHorsPerimetreValidator(),
                    complementAdresseValidator(),
                    codePostalInseeCoupleValidator(),
                ],
            },
        );
    }

    async valid() {
        if (this.searchFormGroup.valid) {
            const formValue = this.searchFormGroup.value;
            const toSend: Partial<SearchpointCritereModel> = {
                adresseInstallation: {
                    batiment: formValue.batiment || undefined,
                    codeInseeCommune: formValue.codeInseeCommune || undefined,
                    codePostal: formValue.codePostal || undefined,
                    escalierEtEtageEtAppartement:
                        formValue.escalierEtEtageEtAppartement || undefined,
                    numeroEtNomVoie: formValue.numeroEtNomVoie || undefined,
                    lieuDit: formValue.lieuDit || undefined,
                },
                categorieClientFinalCode: formValue.categorieClientFinalCode || undefined,
                domaineTensionAlimentationCode: formValue.domaineTensionCode || undefined,
                matriculeOuNumeroSerie: formValue.matriculeOuNumeroSerie || undefined,
                nomClientFinalOuDenominationSociale:
                    formValue.nomClientFinalOuDenominationSociale || undefined,
                numSiret: formValue.numSiret || undefined,
                rechercheHorsPerimetre: formValue.rechercheHorsPerimetre || undefined,
            };

            try {
                const result: EnedisSearchPointModel[] =
                    await this.enedisService.searchPoints(toSend);
                if (!result || result.length < 1) {
                    this.alertService.warning(
                        'Pas de résultats',
                        "Aucun point n'a été retourné par ENEDIS",
                    );
                }
                this.resultTableDataSource = new MatTableDataSource<any>(
                    result.map((r) => ({
                        pdlNumber: r.attributes.id,
                        adresse: `${r.adresseInstallationNormalisee.ligne1 || ''} ${r.adresseInstallationNormalisee.ligne2 || ''} ${r.adresseInstallationNormalisee.ligne3 || ''} ${r.adresseInstallationNormalisee.ligne4 || ''} ${r.adresseInstallationNormalisee.ligne5 || ''} ${r.adresseInstallationNormalisee.ligne6 || ''} ${r.adresseInstallationNormalisee.ligne7 || ''} `,
                        etatContractuel: r.etatContractuel.libelle,
                        matricule: r.matricule,
                        nomClientFinalOuDenominationSociale: r.nomClientFinalOuDenominationSociale,
                        numeroSerie: r.numeroSerie,
                        typeComptage: `${r.typeComptage?.libelle || ''} (${r.typeComptage?.attributes?.code})`,
                    })),
                );
            } catch (error: any) {
                console.log(error);
                const str = error?.error?.message;
                if (str) {
                    // Regex pour extraire l’attribut "code" et le texte de la balise
                    const regex = /<resultat\s+code="([^"]+)">(.*?)<\/resultat>/s;

                    const match = str.match(regex);
                    let code,
                        texte = '';
                    if (match) {
                        code = match[1];
                        texte = match[2].trim();
                        console.log({ code, texte });
                    }

                    this.alertService.error('Une erreur est survenue', `${code}: ${texte}`);
                } else {
                    this.alertService.error('Une erreur est survenue', ``);
                }
                this.resultTableDataSource = new MatTableDataSource<any>();
            }
        } else {
            console.log('Formulaire invalide');
            this.searchFormGroup.markAllAsTouched();
            this.resultTableDataSource = new MatTableDataSource<any>();
        }
    }

    private inArrayValidator(options: string[]): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            return value && !options.includes(value)
                ? { invalidValue: { value, expected: options } }
                : null;
        };
    }
}

// 1. Soit SIRET valide, soit adresse principale complète
export function siretOuAdresseValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
        const siret = group.get('numSiret')?.value;
        const cp = group.get('codePostal')?.value;
        const insee = group.get('codeInseeCommune')?.value;
        const voie = group.get('numeroEtNomVoie')?.value;

        const siretOK = !!siret && /^\d{14}$/.test(siret);
        const adresseOK = !!cp && !!insee && !!voie;

        return siretOK || adresseOK ? null : { siretOuAdresse: true };
    };
}

// 2. Si recherche hors périmètre activée, impose adresse complète + nom ≥ 3 caractères
export function rechercheHorsPerimetreValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
        const horsPerimetre = group.get('rechercheHorsPerimetre')?.value;
        if (!horsPerimetre) return null;

        const cp = group.get('codePostal')?.value;
        const insee = group.get('codeInseeCommune')?.value;
        const voie = group.get('numeroEtNomVoie')?.value;
        const nomClient = group.get('nomClientFinalOuDenominationSociale')?.value;

        const errors: ValidationErrors = {};
        if (!cp || !insee || !voie) {
            errors['adresseIncompletHorsPerimetre'] = true;
        }
        if (!nomClient || nomClient.length < 3) {
            errors['nomClientHorsPerimetre'] = true;
        }

        return Object.keys(errors).length > 0 ? errors : null;
    };
}

// 3. Compléments d'adresse ne doivent pas être utilisés seuls
export function complementAdresseValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
        const voie = group.get('numeroEtNomVoie')?.value;
        const nomClient = group.get('nomClientFinalOuDenominationSociale')?.value;

        const escalier = group.get('escalierEtEtageEtAppartement')?.value;
        const batiment = group.get('batiment')?.value;
        const lieuDit = group.get('lieuDit')?.value;

        const hasComplement = escalier || batiment || lieuDit;
        const hasAnchor = voie || nomClient;

        return hasComplement && !hasAnchor ? { complementSansAncrage: true } : null;
    };
}

// 4. Code postal et code INSEE doivent être saisis ensemble
export function codePostalInseeCoupleValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
        const cp = group.get('codePostal')?.value;
        const insee = group.get('codeInseeCommune')?.value;

        if ((cp && !insee) || (!cp && insee)) {
            return { coupleCpInseeIncomplet: true };
        }
        return null;
    };
}
